import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LoginBackground from "@/components/LoginBackground";
import SupportModal from "@/components/SupportModal";
import { Signal, Loader2, Check, Sun, Moon, LifeBuoy } from "lucide-react";

const BENEFITS = [
  "Integração direta com mk-auth e Evolution API",
  "Cobrança agendada, manual e por chatbot em um só lugar",
  "Cada empresa com dados e instância isolados",
];

const THEME_KEY = "cobrazap-login-theme";

function readTheme() {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    return saved === "light" || saved === "dark" ? saved : "dark";
  } catch {
    return "dark";
  }
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState("");
  const [theme, setTheme] = useState(readTheme);
  // O botão de suporte só aparece depois de uma tentativa de login que falhou.
  const [lastError, setLastError] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);
  const [failCount, setFailCount] = useState(0);
  const isDark = theme === "dark";

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* sem armazenamento: o tema só vale nesta visita */
    }
  }, [theme]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/public/branding")
      .then(({ data }) => {
        if (!cancelled && data?.logo_data_uri) setLogo(data.logo_data_uri);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (ex) {
      const msg = formatApiError(ex);
      setErr(msg);
      setLastError(msg);
      setFailCount((n) => n + 1);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500 " +
    "dark:bg-slate-950 dark:border-slate-800 dark:text-white dark:placeholder:text-slate-600";
  const labelClass = "text-slate-600 dark:text-slate-400 text-xs uppercase tracking-[0.15em]";

  return (
    // A classe `dark` fica neste wrapper: só a tela de login muda de tema.
    <div className={isDark ? "dark" : ""}>
      <div className="relative min-h-screen flex overflow-hidden bg-slate-50 text-slate-600 dark:bg-slate-950 dark:text-slate-300 transition-colors duration-300">
        <LoginBackground />

        {/* -------- Alternar tema -------- */}
        <button
          type="button"
          onClick={() => setTheme(isDark ? "light" : "dark")}
          aria-label={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
          title={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
          data-testid="login-theme-toggle"
          className="absolute top-5 right-5 z-20 h-9 px-3 inline-flex items-center gap-2 rounded-full border text-xs font-medium backdrop-blur transition-colors
            border-slate-300 bg-white/70 text-slate-700 hover:bg-white
            dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300 dark:hover:bg-slate-800
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          <span className="hidden sm:inline">{isDark ? "Modo claro" : "Modo escuro"}</span>
        </button>

        {/* -------- Painel esquerdo -------- */}
        <div className="relative z-10 hidden lg:flex flex-1 flex-col justify-between p-14 border-r border-slate-200 dark:border-slate-800/80">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt="CobraZap" className="h-10 w-auto" />
            ) : (
              <>
                <div className="w-10 h-10 rounded-md bg-emerald-500 flex items-center justify-center shrink-0">
                  <Signal className="w-5 h-5 text-emerald-950" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-outfit text-slate-900 dark:text-white text-lg font-bold tracking-tight">
                    CobraZap
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Painel Multi-Tenant</div>
                </div>
              </>
            )}
          </div>

          <div className="max-w-lg space-y-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-700 dark:text-emerald-400 font-semibold">
              Automação de cobrança via WhatsApp
            </div>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight text-slate-900 dark:text-white leading-[1.08]">
              Cobre menos,
              <br />
              receba mais — no automático.
            </h1>
            <ul className="space-y-3 pt-2">
              {BENEFITS.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-slate-600 dark:text-slate-400 text-sm leading-relaxed"
                >
                  <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" strokeWidth={2.5} />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center gap-6 text-xs text-slate-500 dark:text-slate-600">
            <span>mk-auth · MySQL direto</span>
            <span className="w-1 h-1 rounded-full bg-slate-400 dark:bg-slate-700" />
            <span>Evolution API 2.3.7</span>
          </div>
        </div>

        {/* -------- Painel direito: formulário -------- */}
        <div className="relative z-10 flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
              {logo ? (
                <img src={logo} alt="CobraZap" className="h-9 w-auto" />
              ) : (
                <>
                  <div className="w-9 h-9 rounded-md bg-emerald-500 flex items-center justify-center">
                    <Signal className="w-4.5 h-4.5 text-emerald-950" strokeWidth={2.5} />
                  </div>
                  <div className="font-outfit text-slate-900 dark:text-white text-lg font-bold tracking-tight">
                    CobraZap
                  </div>
                </>
              )}
            </div>

            <form
              onSubmit={submit}
              data-testid="login-form"
              className="w-full backdrop-blur-xl rounded-xl p-8 space-y-6 border transition-colors duration-300
                bg-white/75 border-slate-200 shadow-[0_20px_60px_-20px_rgba(5,150,105,0.25)]
                dark:bg-slate-900/60 dark:border-slate-800 dark:shadow-[0_0_60px_-15px_rgba(16,185,129,0.15)]"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Acesso</div>
                <h2 className="font-outfit text-slate-900 dark:text-white text-2xl font-bold tracking-tight mt-1">
                  Entrar no painel
                </h2>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className={labelClass}>
                    E-mail
                  </Label>
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="email"
                    placeholder="voce@empresa.com.br"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className={labelClass}>
                    Senha
                  </Label>
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              {err && (
                <div
                  data-testid="login-error"
                  className="text-sm text-red-600 dark:text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-3 py-2"
                >
                  {err}
                </div>
              )}

              <Button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold h-11"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
              </Button>
            </form>

            {lastError && (
              <button
                type="button"
                onClick={() => setSupportOpen(true)}
                data-testid="login-support-open-btn"
                className="sm-appear sm-pulse mt-5 w-full h-11 rounded-md border inline-flex items-center justify-center gap-2 text-sm font-semibold backdrop-blur transition-colors
                  border-emerald-500/60 bg-white/70 text-emerald-700 hover:bg-emerald-500/10
                  dark:bg-slate-900/50 dark:text-emerald-300
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
              >
                <LifeBuoy className="w-4 h-4" />
                Suporte
              </button>
            )}

            <p className="text-center text-xs text-slate-500 dark:text-slate-600 mt-6">
              Acesso apenas para empresas cadastradas. Fale com o administrador do CobraZap para criar sua conta.
            </p>
          </div>
        </div>
      </div>

      <SupportModal
        open={supportOpen}
        onOpenChange={setSupportOpen}
        email={email}
        error={lastError}
        isDark={isDark}
        attemptCount={failCount}
      />
    </div>
  );
}
